import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getAppointments, getMockAppointments } from '../../services/appointmentService';
import { getNotifications, getMockNotifications } from '../../services/notificationService';
import AppointmentList from '../../components/calendar/AppointmentList';
import NotificationList from '../../components/notifications/NotificationList';
import './Dashboard.css';

const Dashboard = () => {
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState({
    appointments: true,
    notifications: true
  });
  const [error, setError] = useState({
    appointments: null,
    notifications: null
  });
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    fetchTodayAppointments();
    fetchRecentNotifications();
  };
  
  const fetchTodayAppointments = async () => {
    try {
      setLoading(prev => ({ ...prev, appointments: true }));
      
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);
      
      const data = await getAppointments(startOfDay, endOfDay);
      setTodayAppointments(data || []);
      setError(prev => ({ ...prev, appointments: null }));
    } catch (err) {
      console.error(err);
      // En caso de error, intentamos obtener datos mock directamente
      try {
        const mockData = getMockAppointments(new Date(), new Date());
        setTodayAppointments(mockData);
        setError(prev => ({ ...prev, appointments: null }));
      } catch (mockErr) {
        setError(prev => ({ ...prev, appointments: 'Error al cargar las citas' }));
      }
    } finally {
      setLoading(prev => ({ ...prev, appointments: false }));
    }
  };
  
  const fetchRecentNotifications = async () => {
    try {
      setLoading(prev => ({ ...prev, notifications: true }));
      
      // Obtener notificaciones de los últimos 3 días
      const today = new Date();
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(today.getDate() - 3);
      
      const filters = {
        dateFrom: format(threeDaysAgo, 'yyyy-MM-dd'),
        dateTo: format(today, 'yyyy-MM-dd')
      };
      
      const data = await getNotifications(filters);
      // Mostrar solo las 5 más recientes
      setRecentNotifications((data || []).slice(0, 5));
      setError(prev => ({ ...prev, notifications: null }));
    } catch (err) {
      console.error(err);
      // En caso de error, intentamos obtener datos mock directamente
      try {
        const mockData = getMockNotifications();
        setRecentNotifications(mockData.slice(0, 5));
        setError(prev => ({ ...prev, notifications: null }));
      } catch (mockErr) {
        setError(prev => ({ ...prev, notifications: 'Error al cargar las notificaciones' }));
      }
    } finally {
      setLoading(prev => ({ ...prev, notifications: false }));
    }
  };
  
  return (
    <div className="dashboard-container">
      <h1 className="page-title">Panel</h1>
      
      <div className="dashboard-sections">
        <div className="dashboard-section appointments-section">
          <div className="section-header">
            <h2 className="section-title">Citas para hoy</h2>
            <button 
              className="calendar-link"
              onClick={() => window.open('https://calendar.google.com', '_blank')}
            >
              Mostrar en Calendar
            </button>
          </div>
          
          {loading.appointments ? (
            <div className="loading-state">
              <p>Cargando citas...</p>
            </div>
          ) : error.appointments ? (
            <div className="error-state">
              <p>{error.appointments}</p>
              <button onClick={fetchTodayAppointments}>Reintentar</button>
            </div>
          ) : (
            <AppointmentList appointments={todayAppointments} />
          )}
        </div>
        
        <div className="dashboard-section notifications-section">
          <div className="section-header">
            <h2 className="section-title">Notificaciones recientes</h2>
            <a href="/notifications" className="view-all-link">
              Mostrar todas
            </a>
          </div>
          
          {loading.notifications ? (
            <div className="loading-state">
              <p>Cargando notificaciones...</p>
            </div>
          ) : error.notifications ? (
            <div className="error-state">
              <p>{error.notifications}</p>
              <button onClick={fetchRecentNotifications}>Reintentar</button>
            </div>
          ) : (
            <NotificationList notifications={recentNotifications} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;